const LOCALE_PATTERN = /^[a-z]{2,3}(?:[-_][a-z]{2,4})?$/i;
const URL_LANGUAGE_PATTERN = /^[a-z]{2,3}$/i;
const NORWEGIAN_CODES = new Set(['nb', 'nn', 'no']);
const URL_LANGUAGE_CODES = new Set([
    'da',
    'de',
    'en',
    'es',
    'fi',
    'fr',
    'it',
    'nb',
    'nl',
    'nn',
    'no',
    'pl',
    'pt',
    'sv',
]);
let widgetLocaleSnapshot = null;
export function getWidgetLocale(options = {}) {
    const nav = options.navigator || safeNavigator();
    const browserLocales = getBrowserLocales(nav);
    const pageLocale = getPageLocale(options.document || safeDocument(), options.location || safeLocation());
    if (pageLocale?.source === 'url') {
        return pageLocale.locale;
    }
    if (pageLocale?.locale) {
        return pageLocale.locale;
    }
    return browserLocales[0] || '';
}
export function getStableWidgetLocale(options = {}) {
    const existing = readWidgetLocaleSnapshot();
    if (existing !== null)
        return existing;
    return getWidgetLocale(options);
}
export function freezeWidgetLocale(options = {}) {
    const existing = readWidgetLocaleSnapshot();
    if (existing !== null)
        return existing;
    return writeWidgetLocaleSnapshot(getWidgetLocale(options));
}
export function resetWidgetLocaleSnapshot() {
    widgetLocaleSnapshot = null;
    try {
        if (typeof window !== 'undefined') {
            delete window.__shineWidgetLocaleSnapshot;
        }
    }
    catch {
        /* window may be unavailable in tests/non-browser environments */
    }
}
export function getExplicitWidgetPageLocale(options = {}) {
    return getPageLocale(options.document || safeDocument(), options.location || safeLocation())?.locale || '';
}
export function areCompatibleLocales(left, right) {
    const leftLanguage = getLanguageFamily(left);
    const rightLanguage = getLanguageFamily(right);
    return leftLanguage.length > 0 && leftLanguage === rightLanguage;
}
function getBrowserLocales(nav) {
    if (!nav)
        return [];
    try {
        const locales = nav.languages && nav.languages.length > 0
            ? Array.from(nav.languages)
            : [nav.language];
        return locales
            .map(normalizeLocale)
            .filter(Boolean);
    }
    catch {
        return [];
    }
}
function getPageLocale(doc, loc) {
    const pathLocale = getLocaleFromPath(loc?.pathname || '');
    if (pathLocale)
        return { locale: pathLocale, source: 'url' };
    const htmlLocale = normalizeLocale(doc?.documentElement?.lang || '');
    if (htmlLocale && isUrlLanguage(htmlLocale))
        return { locale: htmlLocale, source: 'html' };
    return null;
}
function getLocaleFromPath(pathname) {
    const rawSegments = pathname
        .split('/')
        .filter(Boolean)
        .slice(0, 2);
    const firstSegment = normalizeLocale(rawSegments[0] || '');
    const secondSegment = normalizeLocale(rawSegments[1] || '');
    if (isUrlLanguage(firstSegment))
        return firstSegment;
    if (URL_LANGUAGE_PATTERN.test(rawSegments[0] || '') && isUrlLanguage(secondSegment))
        return secondSegment;
    return '';
}
function normalizeLocale(locale) {
    const normalized = String(locale || '').trim().replace('_', '-').toLowerCase();
    return LOCALE_PATTERN.test(normalized) ? normalized : '';
}
function getLanguageFamily(locale) {
    const language = normalizeLocale(locale).split('-')[0];
    return NORWEGIAN_CODES.has(language) ? 'no' : language;
}
function isUrlLanguage(locale) {
    const language = locale.split('-')[0];
    return LOCALE_PATTERN.test(locale) && URL_LANGUAGE_CODES.has(language);
}
function readWidgetLocaleSnapshot() {
    try {
        if (typeof window !== 'undefined' && typeof window.__shineWidgetLocaleSnapshot === 'string') {
            widgetLocaleSnapshot = window.__shineWidgetLocaleSnapshot;
            return widgetLocaleSnapshot;
        }
    }
    catch {
        /* fall back to module-local snapshot */
    }
    return widgetLocaleSnapshot;
}
function writeWidgetLocaleSnapshot(locale) {
    widgetLocaleSnapshot = locale;
    try {
        if (typeof window !== 'undefined') {
            window.__shineWidgetLocaleSnapshot = locale;
        }
    }
    catch {
        /* module-local snapshot is enough when window is unavailable */
    }
    return locale;
}
function safeDocument() {
    try {
        return typeof document !== 'undefined' ? document : null;
    }
    catch {
        return null;
    }
}
function safeLocation() {
    try {
        return typeof location !== 'undefined' ? location : null;
    }
    catch {
        return null;
    }
}
function safeNavigator() {
    try {
        return typeof navigator !== 'undefined' ? navigator : null;
    }
    catch {
        return null;
    }
}
